/**
 * GhostPlayersOverlay - SVG-based ghost player rendering for animation mode.
 *
 * Shows semi-transparent "ghost" images of where players started their routes.
 * Uses PlayerVisual with isGhost=true for consistent rendering.
 */

import { useMemo } from 'react'
import { BlockRenderProvider } from '@/components/blocks/BlockRenderContext'
import { PlayerVisual } from '@/components/blocks/visuals/canvas/PlayerVisual'
import { createPlayerBlock } from '@/adapters/PlayerAdapter'
import { createPixelScaleFactors } from '@/utils/RenderScale'
import type { BlockRenderContextValue } from '@/components/blocks/Types'
import type { FieldCoordinateSystem } from '@/utils/Coordinates'
import type { Coordinate } from '@/types'
import { ANIMATION_DEFAULTS } from '@/types/Animation.types'

interface PlayerState {
	playerId: string
	startPosition: Coordinate
	currentPosition: Coordinate
}

interface GhostPlayersOverlayProps {
	playerStates: PlayerState[]
	coordSystem: FieldCoordinateSystem
	playerInfo: Map<string, { label: string; color: string }>
	viewBox: { width: number; height: number }
}

function hasMovedBeyondThreshold(state: PlayerState): boolean {
	const threshold = ANIMATION_DEFAULTS.GHOST_MOVEMENT_THRESHOLD
	const dx = state.currentPosition.x - state.startPosition.x
	const dy = state.currentPosition.y - state.startPosition.y
	return Math.abs(dx) > threshold || Math.abs(dy) > threshold
}

export function GhostPlayersOverlay({
	playerStates,
	coordSystem,
	playerInfo,
	viewBox,
}: GhostPlayersOverlayProps) {
	const movedPlayers = useMemo(
		() => playerStates.filter(hasMovedBeyondThreshold),
		[playerStates],
	)

	const coordinateTransform = useMemo(
		() => (point: { x: number; y: number }) =>
			coordSystem.feetToPixels(point.x, point.y),
		[coordSystem],
	)

	const scale = useMemo(
		() => createPixelScaleFactors(coordSystem),
		[coordSystem.scale],
	)

	const contextValue: BlockRenderContextValue = useMemo(
		() => ({
			mode: 'animated' as const,
			zoom: 100,
			coordinateTransform,
			viewBox,
			scale,
		}),
		[coordinateTransform, viewBox, scale],
	)

	if (movedPlayers.length === 0) {
		return null
	}

	return (
		<BlockRenderProvider value={contextValue}>
			<g className="ghost-players-overlay">
				{movedPlayers.map((playerState) => {
					const info = playerInfo.get(playerState.playerId)
					const block = createPlayerBlock(
						playerState.playerId,
						playerState.startPosition,
						info,
						{ isGhost: true },
					)
					return (
						<PlayerVisual
							key={`ghost-${playerState.playerId}`}
							block={block}
							animatedPosition={playerState.startPosition}
						/>
					)
				})}
			</g>
		</BlockRenderProvider>
	)
}
